import React, { useCallback, useEffect, useState } from "react";
import { editProject } from "../api/project";
import {
  Box,
  TextField,
  Button,
  Typography,
  useTheme,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  InputAdornment,
  ListItemButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUser } from "../hooks/user";
import { getProjects, getMembers } from "../api/project";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { encrypt } from "../utils/cryptoHelper";
import { Person, Edit, Visibility, Delete } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import GIButton from "../components/Button";

const ProjectsView = () => {
  const user = useUser();
  const theme = useTheme();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [projectRoles, setProjectRoles] = useState({});
  const [projectToDelete, setProjectToDelete] = useState(null);

  useEffect(() => {
    getProjects(user.id, (data) => {
      // backend may return active as a boolean, number, or string; normalize before comparing
      const activeProjects = data.filter(
        (p) => String(p.active) !== "false" && String(p.active) !== "0"
      );
      setProjects(activeProjects);
      activeProjects.forEach((project) => {
        getMembers(project.project_id, (members) => {
          const currentMember = members?.find(
            (member) =>
              String(member.user_id).toLowerCase() ===
              String(user.id).toLowerCase()
          );
          if (currentMember) {
            setProjectRoles((currentRoles) => ({
              ...currentRoles,
              [project.project_id]: currentMember.role,
            }));
          }
        });
      });
    });
  }, [user.id]);

  const projectOnClick = (project) => {
    window.localStorage.setItem("project", encrypt(JSON.stringify(project)));
    navigate(`/modules/information?id=${project.project_id}`);
  };
  
  const handleDeleteConfirm = () => {
    const project = projectToDelete;
    editProject(
      {
        ...project,
        active: false,
        // backend requires a newer modified_at than the stored value or the UPDATE is silently skipped
        modified_at: new Date().toISOString(),
      },
      () => {
        setProjects((currentProjects) =>
          currentProjects.filter((p) => p.project_id !== project.project_id)
        );
        setProjectToDelete(null);
      },
      (error) => {
        console.error("Failed to delete project:", error);
        setProjectToDelete(null);
      }
    );
  };

  const containerStyle = {
    display: "flex",
    width: "100%",
    height: "100%",
    flexDirection: "column",
    px: 16,
  };

  return (
    <Box sx={containerStyle}>
      <TextField
        label="Search Project"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        sx={{ m: 1, width: "100%" }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
      <List>
        {projects
          .filter(
            (p) =>
              (p.PROJ_NAME.toLowerCase().includes(search.toLowerCase()) ||
              !search) &&
              String(p.active) !== "false" &&
              String(p.active) !== "0"
          )
          .map((project) => {
            const role = projectRoles[project.project_id];
            const canEditProject =
              role === "Project Admin" || role === "Geologist";
            const canDeleteProject = role === "Project Admin";
            return (
            <ListItem key={project.project_id}>
              <ListItemButton
                onClick={() => {
                  window.localStorage.setItem(
                    "project",
                    encrypt(JSON.stringify(project))
                  );
                  navigate(`/modules/data?id=${project.project_id}`);
                }}
              >
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                    <FolderOutlinedIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={project.PROJ_NAME}
                  primaryTypographyProps={{
                    sx: {
                      color:
                        theme.palette.mode === "dark"
                          ? theme.palette.common.white
                          : theme.palette.text.primary,
                    },
                  }}
                  secondary={`${project.PROJ_CLNT} | ${project.PROJ_CONT} | ${project.task_number}`}
                />
                <IconButton
                  aria-label="Manage project members"
                  onClick={(e) => {
                    e.stopPropagation();
                      window.localStorage.setItem(
                        "project",
                        encrypt(JSON.stringify(project))
                      );
                      navigate(
                        `/modules/organization-management?id=${project.project_id}`
                      );
                  }}
                >
                  <Person />
                </IconButton>
                <IconButton
                  aria-label={canEditProject ? "Edit project" : "View project"}
                  onClick={(e) => {
                    e.stopPropagation();
                    projectOnClick(project);
                  }}
                >
                  {canEditProject ? <Edit /> : <Visibility />}
                </IconButton>
                {canDeleteProject && (
                  <IconButton
                    aria-label="Delete project"
                    onClick={(e) => {
      // need to add stopPropagation otherwise it will navigate to other pages when clicking delete 
                      e.stopPropagation();
                      setProjectToDelete(project);
                    }}
                  >
                    <Delete />
                  </IconButton>
                )}
              </ListItemButton>
            </ListItem>
            );
          })}
      </List>

      <Dialog open={!!projectToDelete} onClose={() => setProjectToDelete(null)}>
        <DialogTitle>Delete project</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{projectToDelete?.PROJ_NAME}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProjectToDelete(null)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <div
        className="App"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Button
          variant="contained"
          onClick={() => navigate(`/modules/create-project`)}
          sx={{ m: 1, width: "20%" }}
        >
          Add Project
        </Button>
      </div>
    </Box>
  );
};

export default ProjectsView;
